import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        primary: "btn primary-btn text-b3-mid",
        cancel: "btn cancel-btn text-b2-mid",
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        dot: "text-gray-700 hover:text-gray-600 p-1 rounded-full hover:bg-gray-50 transition-colors cursor-pointer",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "secondary-btn bg-green-100 text-gray-800 hover:bg-green-200 border border-green-200 shadow-sm",
        drawer:
          "absolute -right-3 top-1/2 z-50 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border bg-background shadow-md hover:bg-muted focus:outline-none opacity-70 btn",
        dashed:
          "border border-dashed rounded-[12px] flex gap-[8px] items-center justify-center px-[24px] py-[10px] transition-opacity w-full bg-green-2",
        filter:
          "flex items-center justify-between rounded-[4px] border border-[var(--color-green-5)] bg-[#f2f7f5] px-3.5 py-2 shadow-[inset_0_0_3px_2px_rgba(152,205,184,0.2)] transition-colors hover:bg-[var(--color-green-1)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-green-5)] focus-visible:ring-offset-2",
        link: "text-primary underline-offset-4 hover:underline",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        stage:
          "bg-[var(--color-green-9)] text-white hover:bg-[#3d9070] h-8 rounded-[10px] px-3 gap-1 text-[14px] font-medium tracking-[-0.56px]",
        "stage-inactive":
          "bg-[#f0f0f0] text-[var(--color-black-8)] hover:bg-[#e5e5e5] h-8 rounded-[10px] px-3 text-[14px] font-normal tracking-[-0.56px]",

        // 🔹 Shiny & Modern Social Variants
        // We use a lighter 'to' gradient + an inset white shadow to create the "Glass" effect
        // 🔹 Minimalist & Modern Social Variants
        // Facebook: Deep, clean navy-blue feel
        // 🔹 Pro-Tier Connection Variants
        // Facebook (Matches your Primary)
        facebook:
          "bg-[#2F65FF] !text-white border-b-2 border-[#1E4DCC] shadow-[0_2px_4px_rgba(47,101,255,0.3)] hover:bg-[#3D71FF] hover:!text-white active:translate-y-[1px] active:border-b-0 [&_svg]:text-white",

        /** WhatsApp brand green — white label/icon (avoids low-contrast on dark forest) */
        whatsapp:
          "bg-[#25D366] !text-white border-b-2 border-[#1ea952] shadow-[0_2px_4px_rgba(37,211,102,0.35)] hover:bg-[#20bd5a] hover:!text-white active:translate-y-[1px] active:border-b-0 [&_svg]:text-white",

        // Call (The Active Emerald)
        call: "bg-[#4EA376] text-white border-b-2 border-[#3A7A58] shadow-[0_2px_4px_rgba(78,163,118,0.3)] hover:bg-[#5BB885] active:translate-y-[1px] active:border-b-0",
      },
      size: {
        default: "h-10 px-4 py-2 has-[>svg]:px-3 cursor-pointer",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-10",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Button, buttonVariants };
